import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import listingIcon from '../../assets/listing-icon.png';
import keyIcon from '../../assets/key-icon.png';
import ConfirmDialog from '../common/ConfirmDialog';
import { transactionTemplatesService } from '../../services/transactionTemplates.service';
import { countTemplate, sideLabel } from '../../utils/transactionTemplates';
import './TransactionTemplatesView.css';

// Reuses the Transactions page card classes (trans-card*) so both views look the same.
const TemplateCard = ({ template, isPersonal, label, onView, onEdit, onDelete }) => {
  const counts = countTemplate(template);
  return (
    <div className="trans-card tpl-card">
      <div className="trans-card-header">
        <div className="trans-property-icon">
          <img
            src={template.side === 'LISTING' ? listingIcon : keyIcon}
            alt={sideLabel(template.side)}
            style={{ width: 22, height: 22, objectFit: 'contain' }}
          />
        </div>
        <div className="trans-card-info">
          <div className="trans-property-address">{template.name}</div>
          <div className="trans-action-type">
            {label || (isPersonal ? `${template.state} · ${sideLabel(template.side)}` : 'Rialty template')}
          </div>
        </div>
      </div>

      <div className="trans-card-badges">
        <span className="trans-meta-info">
          {counts.folderCount} folders · {counts.documentCount} documents · {counts.partyCount} parties
        </span>
      </div>

      <div className="trans-card-meta tpl-card-actions">
        <button type="button" className="tpl-btn tpl-btn--ghost" onClick={onView}>View</button>
        {isPersonal && (
          <>
            <button type="button" className="tpl-btn tpl-btn--ghost" onClick={onEdit}>Edit</button>
            <button type="button" className="tpl-btn tpl-btn--danger" onClick={onDelete} aria-label={`Delete ${template.name}`}>
              Delete
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Broker / Brokerage templates shown read-only inside My Templates (parent → child only).
const INHERITED_LABELS = { BROKER: 'Broker template', BROKERAGE: 'Brokerage template' };

/** Collapsible section; starts expanded. The action (e.g. Create) sits outside the toggle. */
const TemplateAccordion = ({ title, count, action, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <section className="tpl-section">
      <div className="tpl-section-header">
        <button type="button" className="tpl-accordion-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <span className={`tpl-accordion-chevron${open ? ' is-open' : ''}`} aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </span>
          <h2 className="tpl-section-title">{title}</h2>
          {count != null && <span className="tpl-accordion-count">{count}</span>}
        </button>
        {action}
      </div>
      {open && children}
    </section>
  );
};

const TransactionTemplatesView = ({ agentState }) => {
  const navigate = useNavigate();
  const [inheritedGroups, setInheritedGroups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    transactionTemplatesService.getAvailableTemplates({ state: agentState })
      .then((groups) => { if (!cancelled) setInheritedGroups(groups); })
      .catch((error) => console.error('[TransactionTemplatesView] Failed to load brokerage templates', error));
    return () => { cancelled = true; };
  }, [agentState]);
  const [rialtyTemplates, setRialtyTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [personalTemplates, setPersonalTemplates] = useState([]);
  const [personalState, setPersonalState] = useState('loading'); // loading | ready | error
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);
    // Rialty templates are state-specific; an unknown state asks for all rather than none.
    transactionTemplatesService.getSuggestedTemplates({ state: agentState })
      .then((templates) => { if (!cancelled) setRialtyTemplates(templates); })
      .catch((error) => {
        console.error('[TransactionTemplatesView] Failed to load Rialty templates', error);
        if (!cancelled) setLoadError(true);
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [agentState]);

  const loadPersonal = useCallback(async () => {
    try {
      setPersonalTemplates(await transactionTemplatesService.getPersonalTemplates());
      setPersonalState('ready');
    } catch (error) {
      console.error('[TransactionTemplatesView] Failed to load personal templates', error);
      setPersonalState('error');
    }
  }, []);

  useEffect(() => { loadPersonal(); }, [loadPersonal]);

  // Read-only: only the owner (the Broker / Brokerage) can edit or delete these.
  const inheritedTemplates = inheritedGroups
    .filter((g) => INHERITED_LABELS[g.key])
    .flatMap((g) => (g.templates || []).map((template) => ({ template, label: INHERITED_LABELS[g.key] })));

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await transactionTemplatesService.deletePersonalTemplate(pendingDelete.id);
      setPersonalTemplates((prev) => prev.filter((t) => t.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (error) {
      console.error('[TransactionTemplatesView] Delete failed', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="tpl-view">
      <TemplateAccordion title={`Rialty Templates${agentState ? ` (${agentState})` : ''}`} count={isLoading ? null : rialtyTemplates.length}>
        {isLoading ? (
          <p className="tpl-empty">Loading templates…</p>
        ) : loadError ? (
          <p className="tpl-empty">Couldn't load Rialty templates. Please try again.</p>
        ) : rialtyTemplates.length > 0 ? (
          <div className="trans-grid">
            {rialtyTemplates.map((t) => (
              <TemplateCard key={t.id} template={t} onView={() => navigate(`/templates/${t.id}`)} />
            ))}
          </div>
        ) : (
          <p className="tpl-empty">No Rialty templates are available for your state yet.</p>
        )}
      </TemplateAccordion>

      <TemplateAccordion
        title="My Templates"
        count={personalState === 'ready' ? personalTemplates.length : null}
        action={(
          <button type="button" className="tpl-btn tpl-btn--primary" onClick={() => navigate('/templates/new')}>
            + Create Template
          </button>
        )}
      >
        {personalState === 'loading' ? (
          <p className="tpl-empty">Loading your templates…</p>
        ) : personalState === 'error' ? (
          <p className="tpl-empty">Couldn't load your templates. Please try again.</p>
        ) : personalTemplates.length > 0 ? (
          <div className="trans-grid">
            {personalTemplates.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                isPersonal
                onView={() => navigate(`/templates/${t.id}`)}
                onEdit={() => navigate(`/templates/${t.id}/edit`)}
                onDelete={() => setPendingDelete(t)}
              />
            ))}
          </div>
        ) : (
          <p className="tpl-empty">You haven't created any templates yet.</p>
        )}
      </TemplateAccordion>

      {/* Read-only templates from the user's Brokerage (and Broker); only their owner edits them. */}
      <TemplateAccordion title="Brokerage Templates" count={inheritedTemplates.length}>
        {inheritedTemplates.length > 0 ? (
          <div className="trans-grid">
            {inheritedTemplates.map(({ template: t, label }) => (
              <TemplateCard
                key={t.id}
                template={t}
                label={`${label} · ${t.state} · ${sideLabel(t.side)}`}
                onView={() => navigate(`/templates/${t.id}`, { state: { sourceLabel: label } })}
              />
            ))}
          </div>
        ) : (
          <p className="tpl-empty">Your brokerage hasn't shared any templates yet.</p>
        )}
      </TemplateAccordion>

      {pendingDelete && (
        <ConfirmDialog
          title={`Delete "${pendingDelete.name}"?`}
          body="Transactions that already used this template keep their documents and parties."
          confirmLabel="Delete"
          danger
          busy={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default TransactionTemplatesView;
